import {
  Button,
  Backdrop,
  Box,
  Fade,
  Modal,
  Typography,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import {
  gradientBorder,
  variantTextButton,
} from '../../assets/styles/button.style';
import { axiosInstance } from '../../assets/utils/axiosInstance';
import { Status } from '../../constants/reportConstants';
import { useToastNotificationContext } from '../../hooks/global/alert/useToastNotification';
import { CustomError } from '../../interfaces/ErrorType';
import ModalProps from '../../interfaces/ModalProps';
import { ReportDetailsProp } from '../viewReport/reportDetails';

import { getStatusOptions } from '~/pages/customer/reports/report-details/constants';

interface CloseReportModalProps extends ModalProps {
  setReportDetails: Dispatch<SetStateAction<ReportDetailsProp>>;
}

const CloseReportModalComponent = ({ open, handleClose, setReportDetails }: CloseReportModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  
  const { createToastNotification } = useToastNotificationContext();
  const { reportDetailsId } = useParams();

  const style = {
    flexDirection: 'column',
    borderRadius: '.7rem',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    bgcolor: 'background.paper',
    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px', // Updated box-shadow
    p: 3,
    textAlign: 'center',
  };

  const formik = useFormik({
    initialValues: {
      id: reportDetailsId ?? '',
      reasons: '',
      details: '',
      submit: null,
    },
    validationSchema: Yup.object({
      reasons: Yup.string().required('Required reason.'),
    }),
    onSubmit: async (values, helpers) => {
      setIsLoading(true);
      const body = {
        report: values.id,
        description_text: values.reasons,
        additional_description_text: values.details,
      };

      try {
        const statusOptions = getStatusOptions();
        
        const closeReportService = await axiosInstance().post('/close-report', body);
        const [uploadResult] = await Promise.allSettled([closeReportService]);

        if (uploadResult.status === 'fulfilled') {
          createToastNotification('Report successfully closed.', 'success');
          await new Promise(resolve => setTimeout(resolve, 2000));
          setIsLoading(false);
          setReportDetails((prevDetails) => ({
            ...prevDetails,
            status: Status.CLOSED_WHISTLEBLOWER_INITIATED,
            statusName:
              statusOptions.find(
                (option) => option.id === Status.CLOSED_WHISTLEBLOWER_INITIATED,
              )?.name ?? 'Closed',
          }));

          handleClose();
        } else {
          setIsLoading(false);
          throw new Error(
            `Error submitting the report: ${uploadResult.reason}`,
          );
        }
      } catch (err) {
        helpers.setStatus({ success: false });

        // add this to the interface folder
        const customError = err as CustomError;
        const errorMessage =
          customError.message ||
          // customError.request?.responseText ||
          'Something went wrong, please try again.';
        helpers.setErrors({ submit: errorMessage });
        helpers.setSubmitting(false);
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <Modal
      aria-labelledby='transition-modal-title'
      aria-describedby='transition-modal-description'
      open={open}
      //   onClose={handleClose}  // on exit
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <form noValidate onSubmit={formik.handleSubmit}>
            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: '1rem',
                fontWeight: 'bold',
                lineHeight: '17px',
                letterSpacing: '0em',
                textAlign: 'left',
                mt: '1rem',
                mb: '0.5rem',
              }}
            >
              Are you sure of closing this report?
            </Typography>
            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: '.8rem',
                fontWeight: 500,
                lineHeight: '17px',
                letterSpacing: '0em',
                textAlign: 'left',
                mt: '1rem',
                mb: '0.5rem',
              }}
            >
              Reason for closure
            </Typography>

            <TextField
              multiline
              rows={5}
              id='filled-textarea'
              placeholder='Kindly enter your reason'
              variant='filled'
              fullWidth
              autoComplete='off'
              type='text'
              disabled={isLoading}
              error={!!(formik.touched.reasons && formik.errors.reasons)}
              helperText={formik.touched.reasons && formik.errors.reasons}
              name='reasons'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.reasons}
              sx={{
                ...{
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-root': {
                    borderRadius: '6px',
                    padding: '20px',
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '17.28px',
                    color: '#555555',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                  },
                  mb: '1rem',
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                    {
                      borderBottom: 'none',
                    },
                },
              }}
            />

            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: '.8rem',
                fontWeight: 500,
                lineHeight: '17px',
                letterSpacing: '0em',
                textAlign: 'left',
                mt: '1rem',
                mb: '0.5rem',
              }}
            >
              Additional Information (if any)
            </Typography>

            <TextField
              multiline
              rows={5}
              id='filled-textarea'
              placeholder='Kindly add additional information'
              variant='filled'
              fullWidth
              autoComplete='off'
              type='text'
              disabled={isLoading}
              error={!!(formik.touched.details && formik.errors.details)}
              helperText={formik.touched.details && formik.errors.details}
              name='details'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.details}
              sx={{
                ...{
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-root': {
                    borderRadius: '6px',
                    padding: '20px',
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '17.28px',
                    color: '#555555',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                  },
                  mb: '1rem',
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                    {
                      borderBottom: 'none',
                    },
                },
              }}
            />

            {/* Buttons */}
            <Box sx={{ width: '100%' }}>
              {/* Cancel Button */}
              <Button
                variant='text'
                onClick={handleClose}
                // disabled={isLoading}
                sx={{
                  ...variantTextButton,
                  width: 'calc(50% - 0.2rem)',
                  margin: '1rem .1rem .1rem 0',
                }}
              >
                Cancel
              </Button>

              {/* Submit Button */}
              <Button
                variant='outlined'
                type='submit'
                // disabled={isLoading}
                sx={{
                  ...gradientBorder,
                  color: 'black',
                  width: 'calc(50% - 0.2rem)',
                  margin: '1rem .1rem .1rem 0',
                }}
              >
                Close report
              </Button>

            </Box>
          </form>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CloseReportModalComponent;
